import {
  Address,
  WeiPerEther,
  Zero,
  addressEqual,
  getTotalTokenIds,
} from 'nftx.js';
import WithdrawLiquidityReady from './states/Ready';
import AssetsContainer from './AssetsContainer';
import usePositions from '@/application/read/positions/usePositions';
import useAddress from '@/application/read/web3/useAddress';
import useVault from '@/application/read/vaults/useVault';
import useLiquidityPool from '@/application/read/pools/useLiquidityPool';
import WithdrawLiquidityLoading from './states/Loading';
import { useForm, useWatch } from 'react-hook-form';
import { Values } from './types';
import { useMemo, useState } from 'react';
import { formatEther, parseEther } from 'viem';
import useSellAmount from '@/application/read/swap/useSellAmount';
import WithdrawLiquiditySucess from './states/Success';
import WithdrawLiquidityProcessing from './states/Processing';
import { useModalContext } from '@/presentation/context/modal';
import calculateExitFee from '@/domain/positions/calculateExitFee';
import useIntervalState from '@/presentation/hooks/useIntervalState';
import useWithdrawLiquidity from '@/application/write/positions/useWithdrawLiquidity';

const useExitFee = (lockedUntil: number, vTokenToWithdraw: bigint) => {
  return useIntervalState(
    () => {
      return calculateExitFee('liquidity', lockedUntil, vTokenToWithdraw);
    },
    15000,
    [lockedUntil, vTokenToWithdraw]
  );
};

const WithdrawLiquidityContainer = ({
  positionId,
}: {
  positionId: Address;
}) => {
  const { closeModal } = useModalContext();
  const [state, setState] = useState<'view' | 'processing' | 'success'>('view');
  const [withdraw, { status, error, data }] = useWithdrawLiquidity();
  const userAddress = useAddress();
  const { data: [, positions] = [] } = usePositions({ userAddress });
  const position = positions?.find((p) => addressEqual(p.id, positionId));
  const { data: vault } = useVault({ vaultId: position?.vaultId });
  const { data: pool } = useLiquidityPool(position?.poolId as Address);

  const { control, handleSubmit, setValue } = useForm<Values>({
    defaultValues: { percent: '50', tokenIds: [] },
  });

  const { percent = '50', tokenIds = [] } = useWatch({ control });

  const percentageToWithdraw = useMemo(() => {
    if (percent && !Number.isNaN(percent) && position) {
      return Number(percent) / 100;
    }
    return 0;
  }, [percent, position]);

  const [vTokenToWithdraw, ethToWithdraw] = useMemo(() => {
    let vTokenToWithdraw = Zero;
    let ethToWithdraw = Zero;

    if (position) {
      vTokenToWithdraw += position.claimableVToken;
      ethToWithdraw += position.claimableEth;

      const perc = parseEther(`${percentageToWithdraw}`);
      vTokenToWithdraw += (position.vToken * perc) / WeiPerEther;
      ethToWithdraw += (position.eth * perc) / WeiPerEther;
    }

    return [vTokenToWithdraw, ethToWithdraw] as const;
  }, [percentageToWithdraw, position]);

  const exitFee = useExitFee(position?.lockedUntil || 0, vTokenToWithdraw);

  const maxItems = useMemo(() => {
    return Math.floor(Number(formatEther(vTokenToWithdraw - exitFee)));
  }, [exitFee, vTokenToWithdraw]);

  const { data: price } = useSellAmount(
    {
      buyType: 'NFT',
      buyToken: vault?.asset.id ?? '0x',
      buyTokenIds: tokenIds,
      buyAmount: '0',
      buyVaultId: vault?.vaultId ?? '',
      sellType: 'vTOKEN',
      sellToken: vault?.id ?? '0x',
      sellTokenIds: [],
      userAddress: undefined,
    },
    { enabled: tokenIds.length > 0 }
  );

  // Only show the vault fee if the user has bypassed the timelock
  const vaultFee = useMemo(() => {
    const lockedUntil = position?.lockedUntil ?? 0;
    if (lockedUntil === 0) {
      return price?.feePrice ?? Zero;
    }
    return Zero;
  }, [position?.lockedUntil, price?.feePrice]);

  if (!pool || !vault || !position || !userAddress) {
    return <WithdrawLiquidityLoading />;
  }

  switch (state) {
    case 'success':
      return (
        <WithdrawLiquiditySucess
          onClose={closeModal}
          txHash={data?.receipt?.transactionHash}
        />
      );
    case 'processing':
      return (
        <WithdrawLiquidityProcessing
          position={position}
          tokenIds={tokenIds}
          userAddress={userAddress}
          onCancel={() => setState('view')}
          onComplete={() => setState('success')}
          percentageToWithdraw={percentageToWithdraw}
          symbol={vault.token.symbol}
          withdraw={withdraw}
          status={status}
          error={error}
        />
      );
    default:
      return (
        <WithdrawLiquidityReady
          exitFee={exitFee}
          ethToWithdraw={ethToWithdraw}
          vTokenToWithdraw={vTokenToWithdraw}
          onSubmit={handleSubmit(() => setState('processing'))}
          pool={pool}
          position={position}
          control={control}
          vault={vault}
          itemCount={getTotalTokenIds(tokenIds)}
          vaultFee={vaultFee}
          premiumFee={price?.premiumPrice ?? Zero}
          assets={
            vTokenToWithdraw >= WeiPerEther ? (
              <AssetsContainer
                control={control}
                maxItems={maxItems}
                setValue={setValue}
                vaultId={vault.vaultId}
              />
            ) : undefined
          }
        />
      );
  }
};

export default WithdrawLiquidityContainer;
